import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  Handle,
  MarkerType,
  Position,
  ReactFlowProvider,
  type Edge,
  type Node,
  type NodeProps,
  type NodeTypes,
} from 'reactflow'
import 'reactflow/dist/style.css'
import {
  ArrowRight, CheckCircle, Circle, CircleNotch, Clock, PauseCircle, WarningCircle,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react'
import { workflowsApi } from '@/lib/api'
import { useThemeStore } from '@/stores/themeStore'
import { cn } from '@/lib/utils'
import { FALLBACK_META, NODE_META, edgeLabel, humanizeType, subtitleOf } from '@/lib/workflowNodeMeta'
import {
  STAGE_VISUAL_HINT,
  STAGE_VISUAL_LABEL,
  stageVisual,
  stageVisualChipClass,
  stageVisualDotClass,
  type CampaignStageVisual,
} from '@/lib/campaignStatus'
import type { CampaignStage } from '@/types/campaign'

/**
 * Read-only flowchart of a campaign's workflow, shown inside the "Workflow
 * stages" card. Nodes are rendered with a single neutral, monochrome component
 * (icon + label) so the ONLY color on the canvas is the per-stage status —
 * active / held / done / not_started — which is what tells you where the
 * campaign is right now. Node type is conveyed by icon + label, not color.
 *
 * Data: the workflow graph (nodes/edges with positions) is fetched once via
 * workflowsApi; the live `stages` come from the parent's stage-progress poll and
 * are merged onto each node by node_id. No backend changes.
 */


/**
 * The four visual states a stage can be in, derived from its live counts. This
 * is the single source of truth for color-coding both the nodes and the legend
 * so "where is the campaign right now" reads at a glance:
 *   active      — recipients are being processed here right now (brand indigo)
 *   held        — held for a recipient timezone window (amber)
 *   done        — every recipient that reached it has moved on (emerald)
 *   not_started — nobody has reached it yet (neutral)
 * The API's own `waiting` execution state means *actively held on a delay*, the
 * opposite of "not reached yet", so the word `waiting` is not used as a state
 * name or a user-visible label anywhere in this file.
 */
// The states themselves now come from the shared §60 map in `@/lib/campaignStatus`.
type StageVisual = CampaignStageVisual


interface StageVisualStyle {
  ring: string   // node outline
  glow: string   // node box-shadow (colored glow for active/held, plain card otherwise)
  chip: string   // count-chip border/text
  pill: string   // floating status pill on the node (bg-card chip)
  Icon: PhosphorIcon
  spin?: boolean
}

// Canvas-only decoration (ring, glow, count chip, node pill). Labels, the soft
// side-panel chip and the legend dot all come from `@/lib/campaignStatus`, so
// this table never re-declares them. Stage `active` deliberately shares the
// brand indigo (#5e6ad2 / #a5b4fc) with campaign `running`, and `done` shares
// emerald with campaign `completed` — so the two levels cannot invert.
const STAGE_VISUAL: Record<StageVisual, StageVisualStyle> = {
  active: {
    ring: 'ring-2 ring-[#5e6ad2] dark:ring-[#a5b4fc]/70',
    glow: 'shadow-[0_0_0_4px_rgba(94,106,210,0.16),0_1px_2px_rgba(0,0,0,0.12)]',
    chip: 'border-[#5e6ad2]/50 text-[#5e6ad2] dark:text-[#a5b4fc]',
    pill: 'bg-card text-[#5e6ad2] ring-1 ring-[#5e6ad2]/30 dark:text-[#a5b4fc] dark:ring-[#a5b4fc]/25',
    Icon: CircleNotch,
    spin: true,
  },
  held: {
    ring: 'ring-2 ring-amber-400/80',
    glow: 'shadow-[0_0_0_4px_rgba(245,158,11,0.15),0_1px_2px_rgba(0,0,0,0.12)]',
    chip: 'border-amber-400/50 text-amber-600 dark:text-amber-400',
    pill: 'bg-card text-amber-600 ring-1 ring-amber-400/40 dark:text-amber-400 dark:ring-amber-400/30',
    Icon: PauseCircle,
  },
  done: {
    ring: 'ring-1 ring-emerald-400/50',
    glow: 'shadow-card',
    chip: 'border-emerald-400/50 text-emerald-600 dark:text-emerald-400',
    pill: 'bg-card text-emerald-600 ring-1 ring-emerald-400/40 dark:text-emerald-400 dark:ring-emerald-400/30',
    Icon: CheckCircle,
  },
  failed: {
    ring: 'ring-2 ring-red-400/70',
    glow: 'shadow-[0_0_0_4px_rgba(239,68,68,0.12),0_1px_2px_rgba(0,0,0,0.12)]',
    chip: 'border-red-400/50 text-red-600 dark:text-red-400',
    pill: 'bg-card text-red-600 ring-1 ring-red-400/40 dark:text-red-400 dark:ring-red-400/30',
    Icon: WarningCircle,
  },
  not_started: {
    ring: '',
    glow: 'shadow-card',
    chip: 'border-border text-muted-foreground',
    pill: 'bg-card text-muted-foreground ring-1 ring-border/70 dark:text-[#8a8f98] dark:ring-white/[0.1]',
    Icon: Circle,
  },
}


/**
 * The single neutral node used for every stage. Monochrome icon + label identify
 * the node type; the status (ring color, pulsing halo on the live stage, badge,
 * and count chip) is the only color, so the eye lands on where the campaign is.
 */
const CampaignFlowNode = memo(function CampaignFlowNode({ data, type }: NodeProps) {
  const stage = data?._stage as CampaignStage | undefined
  const v = stageVisual(stage)
  const s = STAGE_VISUAL[v]
  const meta = NODE_META[type] ?? FALLBACK_META
  const { Icon } = meta
  // A delay in "For Event" mode is conceptually a Wait-for-Event step (matches
  // the builder), so label it as such instead of the generic "Delay".
  const kind = type === 'delay' && data?.delay_mode === 'wait_for_event' ? 'Wait for Event' : meta.kind
  const subtitle = subtitleOf(type, data || {})
  const handleCls = '!h-2 !w-2 !border-2 !border-card !bg-muted-foreground/40'

  return (
    <div
      className={cn(
        'relative min-w-[190px] cursor-pointer rounded-2xl border border-border bg-card px-4 py-3',
        s.glow,
        s.ring,
      )}
    >
      <Handle type="target" position={Position.Top} className={handleCls} />

      {/* Status pill (top-left) — themed bg-card chip; textual so it reads without relying on color */}
      <span className={cn('absolute -left-2 -top-2.5 z-10 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-emphasis leading-none tracking-tight shadow-sm', s.pill)}>
        {v === 'active' && (
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#5e6ad2]/60 dark:bg-[#a5b4fc]/60" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#5e6ad2] dark:bg-[#a5b4fc]" />
          </span>
        )}
        {v === 'held' && <PauseCircle weight="fill" className="h-2.5 w-2.5" />}
        {v === 'done' && <CheckCircle weight="fill" className="h-2.5 w-2.5" />}
        {v === 'failed' && <WarningCircle weight="fill" className="h-2.5 w-2.5" />}
        {STAGE_VISUAL_LABEL[v]}
      </span>

      {/* Live recipient count (top-right) */}
      {stage && (
        <span className={cn('absolute -right-2 -top-2.5 z-10 rounded-full border bg-card px-2 py-0.5 text-[11px] font-emphasis leading-none tabular-nums shadow-card', s.chip)}>
          {stage.reached.toLocaleString()}
        </span>
      )}

      <div className="flex items-center gap-2.5">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-muted/70 text-muted-foreground">
          <Icon className="h-4 w-4" weight="bold" />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-emphasis uppercase tracking-wider text-muted-foreground">{kind}</p>
          <p className="truncate text-sm font-medium text-foreground">{data?.label || kind}</p>
        </div>
      </div>
      {subtitle && <p className="mt-2 truncate pl-[46px] text-xs text-muted-foreground">{subtitle}</p>}

      {/* Failures at any step: a compact red chip so a failing node is obvious
          at a glance on the canvas; the panel has the counts and, for Send
          Email, the per-recipient reasons. */}
      {(stage?.failed ?? 0) > 0 && (
        <div className="mt-2 pl-[46px]">
          <span className="inline-flex items-center gap-1 rounded-full bg-red-500/10 px-2 py-0.5 text-[10px] font-emphasis text-red-600 dark:bg-red-500/[0.15] dark:text-[#f87171]">
            <WarningCircle weight="fill" className="h-2.5 w-2.5" />
            {stage!.failed.toLocaleString()} failed
          </span>
        </div>
      )}

      {/* Footer: when the last recipient finished this node (saved completed_at).
          Full-bleed hairline divider + right-aligned timestamp reads as a proper
          metadata row rather than a cramped afterthought. */}
      {formatCompletedAtShort(stage?.completed_at) && (
        <div className="-mx-4 mt-2.5 flex items-center gap-1.5 border-t border-border/60 px-4 pt-2">
          {stage?.status === 'completed' ? (
            <CheckCircle weight="fill" className="h-3 w-3 flex-shrink-0 text-emerald-500 dark:text-emerald-400" />
          ) : (
            <Clock weight="bold" className="h-3 w-3 flex-shrink-0 text-muted-foreground/60" />
          )}
          <span className="text-[9px] font-emphasis uppercase tracking-wider text-muted-foreground/60">
            {stage?.status === 'completed' ? 'Completed' : 'Last run'}
          </span>
          <span className="ml-auto flex-shrink-0 text-[10px] font-medium tabular-nums text-foreground/75">
            {formatCompletedAtShort(stage?.completed_at)}
          </span>
        </div>
      )}

      <Handle type="source" position={Position.Bottom} className={handleCls} />
    </div>
  )
})

// Every node type maps to the same neutral component; stable reference for ReactFlow.
const READONLY_NODE_TYPES: NodeTypes = Object.fromEntries(
  Object.keys(NODE_META).map(t => [t, CampaignFlowNode]),
) as NodeTypes


function formatResume(iso: string | null | undefined, nowMs: number): string | null {
  if (!iso) return null
  const target = new Date(iso).getTime()
  if (Number.isNaN(target)) return null
  const diffMin = Math.round((target - nowMs) / 60000)
  if (diffMin <= 1) return 'resuming now'
  if (diffMin < 60) return `resumes in ~${diffMin}m`
  const h = Math.floor(diffMin / 60)
  const m = diffMin % 60
  return m > 0 ? `resumes in ~${h}h ${m}m` : `resumes in ~${h}h`
}

// Absolute timestamp for when a stage last completed (e.g. "24 Jun 2026, 3:42 PM").
// Used in the side panel where there's room for the full date.
function formatCompletedAt(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

// Compact variant for the on-canvas node footer (e.g. "24 Jun, 3:42 PM"); drops
// the year so it stays on one line inside the narrow node card.
function formatCompletedAtShort(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleString(undefined, {
    day: '2-digit', month: 'short', hour: 'numeric', minute: '2-digit',
  })
}

interface RawNode {
  id: string
  type: string
  position: { x: number; y: number }
  data: Record<string, any>
}
interface RawEdge {
  id: string
  source: string
  target: string
  sourceHandle?: string | null
  targetHandle?: string | null
  type?: string
}

// Side-panel status pill — same semantic scheme as the nodes/legend, soft variant.
const StatusBadge: React.FC<{ visual: StageVisual }> = ({ visual }) => {
  const s = STAGE_VISUAL[visual]
  const { Icon } = s
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium', stageVisualChipClass(visual))}>
      <Icon weight={visual === 'done' || visual === 'held' || visual === 'failed' ? 'fill' : 'bold'} className={cn('h-3 w-3', s.spin && 'animate-spin')} />
      {STAGE_VISUAL_LABEL[visual]}
    </span>
  )
}

/** Right-hand detail panel for the selected node. */
const NodePanel: React.FC<{
  stage?: CampaignStage
  label: string
  nodeType?: string
  outgoing: { label?: string; target: string; count?: number }[]
}> = ({ stage, label, nodeType, outgoing }) => {
  const [nowMs, setNowMs] = useState(() => Date.now())
  useEffect(() => {
    if (!stage || stage.held <= 0) return
    const t = setInterval(() => setNowMs(Date.now()), 30000)
    return () => clearInterval(t)
  }, [stage])

  const finished = stage ? Math.max(0, stage.reached - stage.in_progress) : 0
  const pct = stage && stage.reached > 0 ? Math.min(100, Math.round((finished / stage.reached) * 100)) : 0
  const activeNotHeld = stage ? Math.max(0, stage.in_progress - stage.held) : 0
  const resumeLabel = stage && stage.held > 0 ? formatResume(stage.held_until, nowMs) : null

  const rows = stage
    ? [
        { label: 'Completed', value: stage.completed, dot: 'bg-emerald-500 dark:bg-emerald-400' },
        { label: 'In progress', value: activeNotHeld, dot: 'bg-[#5e6ad2] dark:bg-[#a5b4fc]' },
        { label: 'Exited', value: stage.exited, dot: 'bg-muted-foreground/50' },
        { label: 'Failed', value: stage.failed, dot: 'bg-red-500 dark:bg-red-400' },
      ]
    : []

  return (
    <div className="flex h-full flex-col gap-3 rounded-xl border border-border/50 bg-muted/20 p-4">
      <div>
        <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70">Selected stage</p>
        <p className="mt-1 text-sm font-emphasis text-foreground">{label}</p>
        <div className="mt-1.5"><StatusBadge visual={stageVisual(stage)} /></div>
      </div>

      {stage ? (
        <>
          <div>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{pct}% processed</span>
              <span className="tabular-nums">{stage.reached.toLocaleString()} of {stage.total.toLocaleString()} reached</span>
            </div>
            <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-muted/60">
              <div className="h-2 rounded-full bg-[#5e6ad2] transition-all duration-500 dark:bg-[#a5b4fc]" style={{ width: `${pct}%` }} />
            </div>
          </div>

          <div className="space-y-0.5">
            {rows.map(r => (
              <div key={r.label} className="flex items-center justify-between border-b border-border/30 py-1.5 text-xs last:border-0">
                <span className="flex items-center gap-2 text-muted-foreground">
                  <span className={cn('h-1.5 w-1.5 rounded-full', r.dot)} />
                  {r.label}
                </span>
                <span className="font-medium tabular-nums text-foreground">{r.value.toLocaleString()}</span>
              </div>
            ))}
            {stage.not_started > 0 && (
              <div className="flex items-center justify-between py-1.5 text-xs">
                <span className="text-muted-foreground">Not reached</span>
                <span className="font-medium tabular-nums text-foreground">{stage.not_started.toLocaleString()}</span>
              </div>
            )}
          </div>

          {/* For Send Email failures, jump to the Recipients list where each
              failure shows its short reason (full message on hover). */}
          {nodeType === 'sendEmail' && stage.failed > 0 && (
            <button
              type="button"
              onClick={() =>
                document
                  .getElementById('campaign-recipients')
                  ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }
              className="inline-flex items-center gap-1.5 rounded-md bg-red-50 px-2.5 py-1.5 text-xs font-medium text-red-700 ring-1 ring-red-500/20 transition-colors hover:bg-red-100 dark:bg-red-500/10 dark:text-[#f87171] dark:ring-red-400/20 dark:hover:bg-red-500/[0.15]"
            >
              <WarningCircle weight="fill" className="h-3.5 w-3.5 shrink-0" />
              View {stage.failed.toLocaleString()} failed {stage.failed === 1 ? 'recipient' : 'recipients'}
              <ArrowRight className="h-3 w-3 shrink-0" />
            </button>
          )}

          {formatCompletedAt(stage.completed_at) && (
            <div className="flex items-center gap-2.5 rounded-lg border border-emerald-500/20 bg-emerald-50/70 px-3 py-2 dark:bg-emerald-500/[0.07]">
              <CheckCircle weight="fill" className="h-4 w-4 shrink-0 text-emerald-500 dark:text-emerald-400" />
              <div className="min-w-0">
                <p className="text-[10px] font-emphasis uppercase tracking-wide text-emerald-700/70 dark:text-emerald-400/70">
                  {stage.status === 'completed' ? 'Completed' : 'Last completed'}
                </p>
                <p className="truncate text-xs font-emphasis tabular-nums text-foreground">{formatCompletedAt(stage.completed_at)}</p>
              </div>
            </div>
          )}

          {stage.held > 0 && (
            <div className="inline-flex items-center gap-1.5 rounded-md bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 ring-1 ring-amber-500/20 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-400/20">
              <PauseCircle weight="fill" className="h-3.5 w-3.5" />
              <span className="tabular-nums">{stage.held.toLocaleString()}</span> paused
              {resumeLabel && <span className="font-normal text-amber-600/90 dark:text-amber-400/80">· {resumeLabel}</span>}
            </div>
          )}
        </>
      ) : (
        <p className="text-xs text-muted-foreground">No recipients have reached this stage yet.</p>
      )}

      {outgoing.length > 0 && (
        <div className="mt-auto border-t border-border/40 pt-3">
          <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70">Outgoing paths</p>
          <div className="space-y-1">
            {outgoing.map((o, i) => (
              <div key={i} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                {o.label && (
                  <span className="rounded bg-muted/70 px-1.5 py-0.5 text-[10px] font-emphasis text-foreground">{o.label}</span>
                )}
                <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground/60" />
                <span className="min-w-0 flex-1 truncate text-foreground">{o.target}</span>
                {typeof o.count === 'number' && <span className="font-medium tabular-nums text-foreground">{o.count.toLocaleString()}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const Inner: React.FC<{ workflowId: number; stages: CampaignStage[] }> = ({ workflowId, stages }) => {
  const isDark = useThemeStore(s => s.actualTheme === 'dark')
  const [graph, setGraph] = useState<{ nodes: RawNode[]; edges: RawEdge[] } | null>(null)
  const [error, setError] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    // skipErrorToast: the `error` branch below already says so inline. A 404
    // here is expected, not exceptional -- the campaigns list joins the
    // workflow on id alone, so a campaign returned to a member who left
    // still shows the name of a workflow that moved with them.
    workflowsApi.getById(workflowId, { skipErrorToast: true })
      .then(res => {
        if (cancelled) return
        const d = res.data || {}
        setGraph({ nodes: Array.isArray(d.nodes) ? d.nodes : [], edges: Array.isArray(d.edges) ? d.edges : [] })
      })
      .catch(() => { if (!cancelled) setError(true) })
    return () => { cancelled = true }
  }, [workflowId])

  // Live per-node stages from the backend keyed by node id. The backend omits the
  // trigger (it isn't an actionable stage), so we synthesize it: every recipient
  // passes the trigger at launch, so it's "completed" once the campaign has
  // started (any downstream stage reached), and "not_started" before that.
  const stagesByNode = useMemo(() => {
    const m: Record<string, CampaignStage> = {}
    for (const s of stages) m[s.node_id] = s
    if (graph) {
      const started = stages.some(s => s.reached > 0)
      const total = stages.reduce((mx, s) => Math.max(mx, s.total), 0)
      for (const n of graph.nodes) {
        if (n.type === 'trigger' && !m[n.id]) {
          m[n.id] = {
            node_id: n.id,
            type: 'trigger',
            label: (n.data?.label as string) || 'Trigger',
            status: started ? 'completed' : 'not_started',
            completed: started ? total : 0,
            exited: 0,
            in_progress: 0,
            held: 0,
            held_until: null,
            failed: 0,
            not_started: started ? 0 : total,
            reached: started ? total : 0,
            total,
          }
        }
      }
    }
    return m
  }, [stages, graph])

  const rfNodes = useMemo<Node[]>(() => {
    if (!graph) return []
    return graph.nodes.map(n => ({
      id: n.id,
      type: n.type,
      position: n.position || { x: 0, y: 0 },
      data: { ...n.data, _stage: stagesByNode[n.id] },
      draggable: false,
      connectable: false,
    }))
  }, [graph, stagesByNode])

  const rfEdges = useMemo<Edge[]>(() => {
    if (!graph) return []
    const enrichIds = new Set(graph.nodes.filter(n => n.type === 'enrichContacts').map(n => n.id))
    const edgeStroke = isDark ? '#3a4d70' : '#cbd5e1'
    return graph.edges.map(e => {
      const isEnrich = enrichIds.has(e.source)
      return {
        id: e.id,
        source: e.source,
        target: e.target,
        // Anchor to each node's single top/bottom handle; the branch is conveyed
        // by the edge label, not by which handle it leaves from.
        type: e.type || 'smoothstep',
        label: edgeLabel(e.sourceHandle),
        labelBgPadding: [6, 3] as [number, number],
        labelBgBorderRadius: 6,
        labelStyle: { fontSize: 11, fontWeight: 590, fill: isDark ? '#e5edf7' : '#0f172a' },
        labelBgStyle: { fill: isDark ? '#0c1320' : '#ffffff', fillOpacity: 0.95 },
        // No marching-ants animation — it repaints continuously and adds lag;
        // enrichment edges are still distinguished by their brand indigo stroke.
        animated: false,
        style: isEnrich ? { stroke: '#5e6ad2', strokeWidth: 2.5 } : { stroke: edgeStroke, strokeWidth: 1.75 },
        markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16, color: isEnrich ? '#5e6ad2' : edgeStroke },
      }
    })
  }, [graph, isDark])

  const nodeLabels = useMemo(() => {
    const m: Record<string, string> = {}
    for (const n of graph?.nodes || []) m[n.id] = (n.data?.label as string) || humanizeType(n.type)
    return m
  }, [graph])

  const selectedStage = selectedId ? stagesByNode[selectedId] : undefined
  const selectedLabel = selectedId ? nodeLabels[selectedId] || 'Stage' : ''
  const selectedType = selectedId ? graph?.nodes.find(n => n.id === selectedId)?.type : undefined
  const outgoing = useMemo(() => {
    if (!selectedId || !graph) return []
    return graph.edges
      .filter(e => e.source === selectedId)
      .map(e => ({
        label: edgeLabel(e.sourceHandle),
        target: nodeLabels[e.target] || e.target,
        count: stagesByNode[e.target]?.reached,
      }))
  }, [selectedId, graph, nodeLabels, stagesByNode])

  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => setSelectedId(node.id), [])
  const onPaneClick = useCallback(() => setSelectedId(null), [])

  if (error) {
    return <p className="py-8 text-center text-sm text-muted-foreground">Couldn't load the workflow diagram.</p>
  }
  if (!graph) {
    return (
      <div className="flex items-center justify-center py-16">
        <CircleNotch className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }
  if (graph.nodes.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">This workflow has no stages to display.</p>
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_260px]">
        <div className="h-[520px] overflow-hidden rounded-xl border border-border/50">
          <ReactFlow
            nodes={rfNodes}
            edges={rfEdges}
            nodeTypes={READONLY_NODE_TYPES}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            nodesDraggable={false}
            nodesConnectable={false}
            nodesFocusable={false}
            elementsSelectable={false}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            minZoom={0.2}
            proOptions={{ hideAttribution: true }}
            className="bg-background"
          >
            <Background variant={BackgroundVariant.Dots} gap={16} size={1} color={isDark ? '#2a2b30' : '#d8dce3'} />
            <Controls showInteractive={false} />
          </ReactFlow>
        </div>
        <NodePanel stage={selectedStage} label={selectedId ? selectedLabel : 'Click a node to inspect'} nodeType={selectedType} outgoing={outgoing} />
      </div>

      {/* Color legend so the status coding reads without prior context */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-1 text-xs text-muted-foreground">
        {(['active', 'held', 'done', 'failed', 'not_started'] as CampaignStageVisual[]).map(v => (
          <span key={v} className="inline-flex items-center gap-1.5">
            <span className={cn('h-2.5 w-2.5 rounded-full', stageVisualDotClass(v), v === 'active' && 'animate-pulse ring-2 ring-[#5e6ad2]/30 dark:ring-[#a5b4fc]/30')} />
            {STAGE_VISUAL_HINT[v]}
          </span>
        ))}
      </div>
    </div>
  )
}

const CampaignWorkflowFlow: React.FC<{ workflowId: number; stages: CampaignStage[] }> = props => (
  <ReactFlowProvider>
    <Inner {...props} />
  </ReactFlowProvider>
)

export default CampaignWorkflowFlow
